import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import { validate, res } from 'react-email-validator';
import { IoClose as Close, IoRefreshOutline as RefreshIcon, IoAddCircleOutline as AddIcon } from 'react-icons/io5';
import { PiIdentificationCardLight as NameCardIcon } from 'react-icons/pi';
import { ReactComponent as DefaultFormPic } from '@Assets/form.svg';
import { ReactComponent as HighFive } from '@Assets/highFive.svg';
import PhoneInput from 'react-phone-input-2'
import FieldPage from '@IssueConsoleForms/FieldPage';
import NameCardForm from '@RegistriesForms/NameCardForm';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxBillingUsage } from '@Hooks/reduxBillingUsageHook/useReduxBillingUsage';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import useToken from '@Root/useToken';
import LinearProgress from '@mui/material/LinearProgress';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import FieldPageService from '@Helpers/URLHelpers/FieldPageService';
import FieldService from '@Helpers/URLHelpers/FieldService';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import RegistryLinkService from '@Helpers/URLHelpers/WeboutService/RegistryLinkService';
import ErrorHandler from '@Root/ErrorHandler';
import 'react-phone-input-2/lib/material.css'
import './TicketForm.scss';

export default function TicketForm(props) {

    const basePageBackgroundStyle = {
        backgroundColor: 'var(--color-background)'
    };

    const baseFieldBackgroundStyle = {
        backgroundColor: 'var(--color-componentBackground)'
    };

    const [openDrawer, setOpenDrawer] = useState(false);
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [fullName, setFullName] = useState('');
    const [pageType, setPageType] = useState('intro');
    const [disableSubmit] = useState(false);
    const [disableNext, setDisableNext] = useState(true);
    const [isNextHovered, setIsNextHovered] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [cursor, setCursor] = useState('wait');
    const [pageIndex, setPageIndex] = useState(0);
    const [details, setDetails] = useState({});
    const [formFieldPageList, setFormFieldPageList] = useState([]);
    const [fieldEntryMap, setFieldEntryMap] = useState({});
    const [ticketCreated, setTicketCreated] = useState(null);
    const [formFieldList, setFormFieldList] = useState([]);
    const [, setFormFieldIdList] = useState([]);
    const [, setFormFieldPageIdList] = useState([]);
    const [, setAdminFieldList] = useState([]);
    const [, setAdminFieldIdList] = useState([]);
    const [kaiziqResolveConnection] = useState(props.kaiziqResolveConnection);
    const [, setFieldBackgroundObj] = useState({});
    const [, setPageHeaderObj] = useState({});
    const [, setPageBackgroundObj] = useState({});
    const [pageBackgroundStyle, setPageBackgroundStyle] = useState({});
    const [fieldBackgroundStyle, setFieldBackgroundStyle] = useState({});
    const [pagePath, setPagePath] = useState([]);
    const [, setActiveUser] = useState({});
    const [nameCardList, setNameCardList] = useState([]);
    const [nameCardLoading, setNameCardLoading] = useState(false);
    const [selectedNameCard, setSelectedNameCard] = useState(null);
    const [showNameCardForm, setShowNameCardForm] = useState(false);
    const nameCardSearchTimeoutRef = useRef(null);
    const { token } = useToken();
    const { activeUser: activeUserObj } = useReduxActiveUser();
    const { isOverLimit, isCreationBlocked, usage } = useReduxBillingUsage();
    let fieldFilter = {
        projectIdList: [],
        tableType: 'kaiziqResolveConnection',
        filterAttribute1List: []
    };
    const fieldPageService = new FieldPageService();
    const fieldService = new FieldService();
    const ticketService = new TicketService();
    const nameCardService = new NameCardService();
    const registryLinkService = new RegistryLinkService();
    const errorHandler = new ErrorHandler();
    const className = 'ticketForm';

    useEffect(() => {
        renderData();
    }, [props.kaiziqResolveConnection])

    const renderData = async ()=> {
        let detailsObj = JSON.parse(props.kaiziqResolveConnection.details);
        setFormFieldPageIdList(detailsObj.fields?.['formPages'])
        setFormFieldIdList(detailsObj.fields?.['form']);
        setAdminFieldIdList(detailsObj.fields?.['admin']);
        setDetails(detailsObj);
        if(!!activeUserObj) {
            setActiveUser(activeUserObj);
        }
        loadNameCards();
        if(detailsObj.settings.formType !== 'feedback') {
            if(!!token && token !== 'void') {
                setEmail(activeUserObj?.email);
                setFullName(activeUserObj?.fullName);
                const initPhone = buildPhoneInputValue(activeUserObj?.contactNumber, activeUserObj?.areaCode);
                setPhoneNumber(initPhone);
                verifyIntroNextButton(activeUserObj?.email, initPhone, activeUserObj?.fullName);
            }
        }
        try {
            let adminFilter = fieldFilter;
            adminFilter.filterAttribute1List = ['admin'];
            adminFilter.projectIdList = [props.kaiziqResolveConnection?.projectId];
            adminFilter.tableId = props.kaiziqResolveConnection?.id;
            let filter = {
                fieldFilter: adminFilter
            }
            let adminFieldListObj = await fieldService.getListFiltered(filter);
            adminFieldListObj = JSON.parse(adminFieldListObj.body);
            setAdminFieldList(adminFieldListObj?.result);
            let formFilter = fieldFilter;
            formFilter.filterAttribute1List = ['form'];
            filter = {
                fieldFilter: formFilter
            }
            let formFieldListObj = await fieldService.getListFiltered(filter);
            formFieldListObj = JSON.parse(formFieldListObj.body);
            setFormFieldList(formFieldListObj?.result);
            let fieldPageListObj = await fieldPageService.getFieldPageListByTable('kaiziqResolveConnection', props.kaiziqResolveConnection.id);
            fieldPageListObj = JSON.parse(fieldPageListObj.body);
            const formPages = JSON.parse(props?.kaiziqResolveConnection?.details)?.fields?.formPages;
            const sortedFieldPageListObj = fieldPageListObj.sort((a, b) => {
                return formPages.indexOf(a.id) - formPages.indexOf(b.id);
            });
            setFormFieldPageList(sortedFieldPageListObj);
            let detailsObj = JSON.parse(props.kaiziqResolveConnection.details);
            setFormFieldPageIdList(detailsObj.fields['formPages'])
            setFormFieldIdList(detailsObj.fields['form']);
            setAdminFieldIdList(detailsObj.fields['admin']);
            setDetails(detailsObj);
            setPageBackgroundObj(detailsObj?.settings?.pageBackground);
            let pageBackgroundObjValue = detailsObj?.settings?.pageBackground;
            setPageHeaderObj(detailsObj?.settings?.pageHeader);
            if(pageBackgroundObjValue.type === 'solid') {
                setPageBackgroundStyle({
                    ...basePageBackgroundStyle
                });
            } else if((pageBackgroundObjValue.type === 'image') &&
                (!!pageBackgroundObjValue.imageURL) && (pageBackgroundObjValue.imageURL !== 'N/A')) {
                setPageBackgroundStyle({
                    ...basePageBackgroundStyle,
                    backgroundImage: 'url("' + pageBackgroundObjValue.imageURL + '")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                });
            } else {
                setPageBackgroundStyle(basePageBackgroundStyle);
            }
            let themeObj = detailsObj?.settings?.theme;
            let fieldBackgroundObjValue = themeObj?.formFieldTheme?.formFieldThemeBackground;
            setFieldBackgroundObj(fieldBackgroundObjValue);
            setFieldBackgroundStyle({
                ...baseFieldBackgroundStyle
            });
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const loadNameCards = async (searchQuery = '') => {
        setNameCardLoading(true);
        try {
            const filter = {
                nameCardFilter: {
                    projectIdList: [props.kaiziqResolveConnection?.projectId],
                    sortBy: 'createTimeStamp',
                    sortAsc: false,
                    searchQuery
                },
                page: 0,
                size: 200
            };
            let result = await nameCardService.getListFiltered(filter);
            result = JSON.parse(result.body);
            setNameCardList(result?.result?.nameCardList || []);
        } catch (err) {
            errorHandler.logError('Name Card Load', err);
        }
        setNameCardLoading(false);
    };

    const handleNameCardInputChange = (e, value, reason) => {
        if (reason !== 'input') return;
        if (nameCardSearchTimeoutRef.current) clearTimeout(nameCardSearchTimeoutRef.current);
        nameCardSearchTimeoutRef.current = setTimeout(() => {
            loadNameCards(DOMPurify.sanitize(value));
        }, 300);
    };

    const buildPhoneInputValue = (contactNumberValue, areaCodeValue) => {
        const normalizedContactNumber = (contactNumberValue || '').toString().replace(/\D/g, '');
        const normalizedAreaCode = (areaCodeValue || '').toString().replace(/\D/g, '');

        if (!normalizedContactNumber) {
            return '';
        }

        if (!normalizedAreaCode || normalizedContactNumber.startsWith(normalizedAreaCode)) {
            return normalizedContactNumber;
        }

        return `${normalizedAreaCode}${normalizedContactNumber}`;
    };

    const handleNameCardChange = (e, value) => {
        setSelectedNameCard(value);
        if (value) {
            const newFullName = value.name || '';
            const newEmail = value.email || '';
            const newAreaCode = value.areaCode || '91';
            const newPhone = buildPhoneInputValue(value.contactNumber, newAreaCode);
            console.log(newPhone);
            console.log(value);
            setFullName(newFullName);
            setEmail(newEmail);
            setPhoneNumber(newPhone);
            verifyIntroNextButton(newEmail, newPhone, newFullName);
        }
    };

    useEffect(() => () => {
        if (nameCardSearchTimeoutRef.current) clearTimeout(nameCardSearchTimeoutRef.current);
    }, []);

    const toggleNameCardForm = () => setShowNameCardForm(prev => !prev);

    const handleNameCardCreated = async () => {
        setShowNameCardForm(false);
        await loadNameCards();
    };

    const handleDrawer = ()=>{
        setOpenDrawer(!openDrawer);
        setPageType('intro');
        props?.onClose();
    }

    const handleEmailTextChange = (e) => {
        let sanitizedEmail = DOMPurify.sanitize(e.target.value);
        setEmail(sanitizedEmail);
        verifyIntroNextButton(sanitizedEmail, phoneNumber, fullName);
    }

    const onPhoneNumberChange = async(newNumber) => {
        setPhoneNumber(newNumber)
        verifyIntroNextButton(email, newNumber, fullName);
    }

    const handleFullNameTextChange = (e) => {
        let sanitizedFullName = DOMPurify.sanitize(e.target.value);
        setFullName(sanitizedFullName);
        verifyIntroNextButton(email, phoneNumber, sanitizedFullName);
    }

    const handleNextClick = async () => {
        let pagePathObj = pagePath;
        if(pageType === 'intro') {
            setPageType('pageLoader');
            setPageType('formPage');
            setPageIndex(0)
            pagePathObj = [...pagePath, -1];
        } else {
            let formFieldPageObj = formFieldPageList[pageIndex];
            let detailsObj = JSON.parse(formFieldPageObj?.details);
            let nextBehaviour = detailsObj?.buttonBehaviour?.next;
            if(nextBehaviour?.behaviour?.action === 'next') {
                setPageType('pageLoader');
                setPageIndex(pageIndex + 1);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            } else if(nextBehaviour?.behaviour?.action === 'goto') {
                let nextPageIndex = formFieldPageList.indexOf(formFieldPageList?.find(formFieldPage => formFieldPage?.id === nextBehaviour?.nextPage?.value));
                setPageType('pageLoader');
                setPageIndex(nextPageIndex);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            } else if(nextBehaviour?.behaviour?.action === 'conditional') {
                let formFieldObj = fieldEntryMap[nextBehaviour?.conditionalField];
                let nextOptionSelectObj = nextBehaviour?.value?.find((valueObj) => valueObj?.option?.id === JSON.parse(formFieldObj));
                let nextPageIndex = formFieldPageList.indexOf(formFieldPageList?.find(formFieldPage => formFieldPage?.id === nextOptionSelectObj?.goto?.value));
                setPageType('pageLoader');
                setPageIndex(nextPageIndex);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            } else if(nextBehaviour?.behaviour?.action === 'submit') {
                await handleSubmitForm();
            } else {
                setPageType('pageLoader');
                setPageIndex(pageIndex + 1);
                setPageType('formPage');
                pagePathObj = [...pagePath, pageIndex];
            }
        }
        setPagePath(pagePathObj);
        setIsNextHovered(false);
    }

    const handleBackClick = async() => {
        let pagePathObj = pagePath;
        let previousIndex = pagePathObj.pop();
        if(previousIndex === -1) {
            setPageType('pageLoader');
            setPageType('intro');
            setPageIndex(-1)
        } else {
            setPageType('pageLoader');
            setPageIndex(previousIndex);
            setPageType('formPage');
        }
    }

    const reportAnotherClick = async() => {
        setCursor('wait');
        setFieldEntryMap({});
        setPageType('intro');
        setPageIndex(0);
        setFullName('');
        setEmail('');
        setPhoneNumber('');
        setTicketCreated(null);
        if(details.settings.formType !== 'feedback') {
            if(!!token && token !== 'void') {
                setEmail(activeUserObj?.email);
                setFullName(activeUserObj?.fullName);
                setPhoneNumber(buildPhoneInputValue(activeUserObj?.contactNumber, activeUserObj?.areaCode));
            }
        }
        setCursor('auto');
    }

    const handleSubmitForm = async() => {
        setCursor('wait');
        setPageType('feedback');
        try {
            let ticketObj = {
                ticket: {
                    kaiziqResolveConnectionId: props.kaiziqResolveConnection.id,
                    fieldEntryObj: fieldEntryMap,
                    createFullName: fullName,
                    createEmail: email,
                    createPhoneNumber: phoneNumber
                }
            }
            ticketObj = await ticketService.createTicket(ticketObj);
            ticketObj = JSON.parse(ticketObj.body);
            setTicketCreated(ticketObj);
            if (selectedNameCard) {
                await registryLinkService.createRegistryLink({
                    projectId: props.kaiziqResolveConnection.projectId,
                    registryType: 'nameCard',
                    registryId: selectedNameCard.id,
                    targetType: 'ticket',
                    targetId: ticketObj.id,
                });
                // Name cards store their linked organization directly (organization > 0 when
                // set; 0 is the "no organization" sentinel, not a real id) — auto-fill from a
                // name card that belongs to an organization also labels the ticket with that
                // organization, same as if the agent had tagged both by hand.
                if (selectedNameCard.organization > 0) {
                    await registryLinkService.createRegistryLink({
                        projectId: props.kaiziqResolveConnection.projectId,
                        registryType: 'orgCard',
                        registryId: selectedNameCard.organization,
                        targetType: 'ticket',
                        targetId: ticketObj.id,
                    });
                }
            }
            window.dispatchEvent(new CustomEvent('ticket:created'));
        } catch (err) {
            errorHandler.handleError(err);
        }
        if(details.settings.formType !== 'feedback') {
            handleDrawer();
            reportAnotherClick();
        }
        setCursor('auto');
    }

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    function emailValidCheck(email){
        validate(email);
        if(res){
            return true;
        } else {
            return false;
        }
    }

    const verifyIntroNextButton = (checkEmail, checkPhoneNumber, checkFullName) => {
        let isNextDisabledBoolean = false;
        if(details?.settings?.defaults?.fullName) {
            if(checkFullName?.length === 0) isNextDisabledBoolean = true;
        }
        if(details?.settings?.defaults?.email) {
            if(checkEmail?.length === 0 || !emailValidCheck(checkEmail)) isNextDisabledBoolean = true;
        }
        if(details?.settings?.defaults?.phoneNumber) {
            if(checkPhoneNumber?.length === 0) isNextDisabledBoolean = true;
        }
        setDisableNext(isNextDisabledBoolean);
        setRefresh(!refresh);
    }

    const handleFieldEntryChange = (fieldId, value) => {
        let fieldEntryMapObj = fieldEntryMap;
        fieldEntryMapObj[fieldId] = value;
        setFieldEntryMap(fieldEntryMapObj);
    }

    return (
        <>
        <Drawer
            size={'lg'}
            anchor='right'
            onClose={props?.onClose}
            open={props?.open}
            className={className}>
                <div className={className + '__body__inner'}>
                    <div className={className + '__body__inner__top flex-row-center-space-between'}>
                        <div className={className + '__body__inner__top__t1'}>
                            Create Tickets
                        </div>
                        <Close className={className + '__body__inner__top__close close-button'}
                            onClick={handleDrawer}/>
                    </div>
                    {isOverLimit && (
                        <div className={`${className}__overageBanner${isCreationBlocked ? ` ${className}__overageBanner--blocked` : ''}`}>
                            <span>{isCreationBlocked ? '🚫' : '⚠'}</span>
                            <span>
                                {isCreationBlocked
                                    ? usage?.ticketLimitMode === 'hard'
                                        ? <><strong>Ticket creation blocked.</strong> You've reached your hard limit of <strong>{usage?.planLimitSnapshot} tickets</strong> this month.</>
                                        : <><strong>Ticket creation blocked.</strong> You have unpaid overage from last month. Please clear your balance first.</>
                                    : <>You've reached your <strong>{usage?.planLimitSnapshot}-ticket</strong> monthly limit. This ticket will be counted as <strong>overage</strong> and billed accordingly.</>
                                }
                            </span>
                        </div>
                    )}
                    {
                        cursor === 'wait' ?
                            <div className={className + '__body__inner__formBody__form__loader width-wfa'}>
                                <LinearProgress className={className + '__body__inner__formBody__form__loader__l1 thick-loader width-wfa grey-text'} color='inherit'/>
                            </div>
                        :
                            pageType === 'feedback' &&
                                (!!ticketCreated && Object.keys(ticketCreated).length !== 0) &&
                                    <div className={className + '__body__inner__formBody flex-column-center-2vh-gap width-wfa'} style={pageBackgroundStyle !== null ? pageBackgroundStyle : {} }>
                                        <div className={className + '__body__inner__formBody__created__inner flex-column-center'}
                                            style={(!!kaiziqResolveConnection && Object.keys(kaiziqResolveConnection) !== 0) ? fieldBackgroundStyle : {}}>
                                            <div className={className + '__body__inner__formBody__created__t1 standard-text-large bold-text'}>
                                                Your submission has been recorded!
                                            </div>
                                            <HighFive className={className + '__body__inner__formBody__created__graphic'}/>
                                            {
                                                details?.settings?.defaults?.generateTicketId &&
                                                    <div className={className + '__body__inner__formBody__created__t2 standard-text-large flex-row-center-1vw-gap'}>
                                                        Ticket Id
                                                        <div className={className + '__body__inner__formBody__created__tag semi-bold-text standard-link '} >
                                                            {ticketCreated?.ticketId}
                                                        </div>
                                                    </div>
                                            }
                                            <div className={className + '__body__inner__formBody__created__button standard-link'} onClick={reportAnotherClick}>
                                                Report Another
                                            </div>
                                        </div>
                                    </div>
                    }
                    {
                        cursor !== 'wait' &&
                        pageType === 'intro' &&
                            <div className={className + '__body__inner__formBody flex-column-center-2vh-gap width-wfa'} style={pageBackgroundStyle !== null ? pageBackgroundStyle : {} }>
                                <div className={className + '__body__inner__formBody__page__inner flex-column-center-2vh-gap'}>
                                    <div className={className + '__body__inner__formBody__page__inner__header '} style={(!!kaiziqResolveConnection && Object.keys(kaiziqResolveConnection) !== 0) ? fieldBackgroundStyle : {}}>
                                        <h1 className={className + '__body__inner__formBody__page__inner__header__title flex-row-center-1vw-gap standard-text-weight'}>
                                            {
                                                !!kaiziqResolveConnection?.photo ?
                                                <img
                                                    src={URL.createObjectURL(dataURLtoFile(kaiziqResolveConnection?.photo))}
                                                    className={className + '__body__inner__formBody__page__inner__header__title__photo'}
                                                    alt='Form Thumbnail'/> :
                                                <DefaultFormPic className={className + '__body__inner__formBody__page__inner__header__title__photo opacity-0p6'}/>
                                            }
                                            {kaiziqResolveConnection?.title}
                                        </h1>
                                        <div className={className + '__body__inner__formBody__page__inner__header__description'}
                                            dangerouslySetInnerHTML={{__html: kaiziqResolveConnection?.description}}/>
                                    </div>
                                    {/* Name Card auto-fill section */}
                                    <div className={className + '__body__inner__formBody__page__inner__nameCard'}
                                        style={(!!kaiziqResolveConnection && Object.keys(kaiziqResolveConnection) !== 0) ? fieldBackgroundStyle : {}}>
                                        <div className={className + '__body__inner__formBody__page__inner__nameCard__header'}>
                                            <NameCardIcon className={className + '__body__inner__formBody__page__inner__nameCard__icon'} />
                                            <span className={className + '__body__inner__formBody__page__inner__nameCard__label'}>Auto-fill from Name Card</span>
                                        </div>
                                        <div className={className + '__body__inner__formBody__page__inner__nameCard__row'}>
                                            <Autocomplete
                                                options={nameCardList}
                                                value={selectedNameCard}
                                                onChange={handleNameCardChange}
                                                onInputChange={handleNameCardInputChange}
                                                filterOptions={(options) => options}
                                                loading={nameCardLoading}
                                                getOptionLabel={(option) => option.name + (option.email ? ` — ${option.email}` : '')}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                className={className + '__body__inner__formBody__page__inner__nameCard__autocomplete'}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder='Search name cards…'
                                                        variant='standard'
                                                        className={className + '__body__inner__formBody__page__inner__nameCard__input'}
                                                    />
                                                )}
                                            />
                                            <IconButton
                                                size='small'
                                                onClick={() => loadNameCards()}
                                                disabled={nameCardLoading}
                                                className={className + '__body__inner__formBody__page__inner__nameCard__refresh'}
                                                title='Refresh name cards'
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </div>
                                        <button
                                            type='button'
                                            className={className + '__body__inner__formBody__page__inner__nameCard__createBtn'}
                                            onClick={toggleNameCardForm}
                                        >
                                            <AddIcon />
                                            Create Name Card
                                        </button>
                                    </div>

                                    <div className={className + '__body__inner__formBody__page__inner__fields flex-column-flex-start-2vh-gap'}
                                        style={(!!kaiziqResolveConnection && Object.keys(kaiziqResolveConnection) !== 0) ? fieldBackgroundStyle : {}}>
                                        {
                                            details?.settings?.defaults?.fullName &&
                                                <div className={className + '__body__inner__formBody__page__inner__fields__fullName flex-column-flex-start-2vh-gap width-wfa'}>
                                                    <h2 className={className + '__body__inner__formBody__page__inner__fields__fullName__t1 standard-text-weight ' +
                                                        ((fullName?.length === 0) ? 'red-text' : '')}>
                                                        Name
                                                    </h2>
                                                    <TextField
                                                        className={className + '__body__inner__formBody__page__inner__fields__fullName__textField width-wfa'}
                                                        placeholder='Enter Your Name ...'
                                                        type='text'
                                                        required
                                                        onChange={handleFullNameTextChange}
                                                        value={fullName}
                                                        inputProps={{ maxLength: 500 }}
                                                        maxRows='1'
                                                        variant='standard'/>
                                                </div>
                                        }
                                        {
                                            details?.settings?.defaults?.email &&
                                                <div className={className + '__body__inner__formBody__page__inner__fields__email flex-column-flex-start-2vh-gap width-wfa'}>
                                                    <h2 className={className + '__body__inner__formBody__page__inner__fields__email__t1 standard-text-weight ' +
                                                        ((email?.length === 0 || !emailValidCheck(email)) ? 'red-text' : '')}>
                                                        Email ID
                                                    </h2>
                                                    <TextField
                                                        className={className + '__body__inner__formBody__page__inner__fields__fullName__textField width-wfa'}
                                                        placeholder='Enter Your Email ID ...'
                                                        type='text'
                                                        required
                                                        onChange={handleEmailTextChange}
                                                        value={email}
                                                        inputProps={{ maxLength: 500 }}
                                                        maxRows='1'
                                                        variant='standard'/>
                                                </div>
                                        }
                                        {
                                            details?.settings?.defaults?.phoneNumber &&
                                                <div className={className + '__body__inner__formBody__page__inner__fields__phoneNumber flex-column-flex-start-2vh-gap width-wfa'}>
                                                    <h2 className={className + '__body__inner__formBody__page__inner__fields__phoneNumber__t1 standard-text-weight ' +
                                                        ((phoneNumber?.length < 10) ? 'red-text' : '')}>
                                                        Contact Number
                                                    </h2>
                                                    <div className={className + '__body__inner__formBody__page__inner__fields__phoneNumber'}>
                                                        <PhoneInput
                                                            country='in'
                                                            placeholder='Enter contact number'
                                                            value={phoneNumber}
                                                            specialLabel=''
                                                            disabled={cursor === 'wait'}
                                                            inputStyle={fieldBackgroundStyle}
                                                            onChange={(newNumber) => onPhoneNumberChange(newNumber)}/>
                                                    </div>
                                                </div>
                                        }
                                    </div>
                                    <div className={className + '__body__inner__formBody__page__inner__buttons flex-row-center-space-between'}
                                        style={(!!kaiziqResolveConnection && Object.keys(kaiziqResolveConnection) !== 0) ? fieldBackgroundStyle : {}}>
                                        <div/>
                                        <div className={className + '__body__inner__formBody__page__inner__buttons__right flex-row-center-1vw-gap'}>
                                            {
                                                pageIndex > 1 &&
                                                    <div className={className + '__inner__formBody__page__inner__buttons__right__startAgain standard-link'}
                                                        onClick={reportAnotherClick}>
                                                        Start Again
                                                    </div>
                                            }
                                            <div className={className + '__body__inner__formBody__page__inner__buttons__right__next '}>
                                                <BorderBlueButtonV2
                                                    text='Next'
                                                    disabled={disableNext || isCreationBlocked}
                                                    onClick={handleNextClick}/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    }
                    {
                        cursor !== 'wait' && pageType === 'formPage' &&
                            <div className={className + '__body__inner__formBody__page flex-column-center-2vh-gap width-wfa'} style={pageBackgroundStyle !== null ? pageBackgroundStyle : {} }>
                                <div className={className + '__body__inner__formBody__page__inner flex-column-center-2vh-gap'}>
                                    <div className={className + '__body__inner__formBody__page__inner__fieldList '}>
                                        <FieldPage
                                            index={pageIndex}
                                            fieldPage={formFieldPageList[pageIndex]}
                                            formFieldList={formFieldList}
                                            fieldBackgroundStyle={fieldBackgroundStyle}
                                            handleFieldEntryChange={handleFieldEntryChange}
                                            fieldEntryMap={fieldEntryMap}
                                            isNextHovered={isNextHovered}
                                            setDisableNext={setDisableNext}
                                            kaiziqResolveConnection={kaiziqResolveConnection}/>
                                    </div>
                                    <div className={className + '__body__inner__formBody__page__inner__buttons flex-row-center-space-between'}
                                        style={(!!kaiziqResolveConnection && Object.keys(kaiziqResolveConnection) !== 0) ? fieldBackgroundStyle : {}}>
                                        <div className={className + '__body__inner__formBody__page__inner__buttons__left bold-text grey-text'}>
                                            Page: {pageIndex + 1}/{formFieldPageList.length}
                                        </div>
                                        {
                                            (pageIndex + 1) < formFieldPageList.length && JSON.parse(formFieldPageList[pageIndex]?.details)?.buttonBehaviour?.next?.behaviour?.action !== 'submit' ?
                                                <div className={className + '__body__inner__formBody__page__inner__buttons__right flex-row-center-1vw-gap'}>
                                                    <div className={className + '__body__inner__formBody__page__inner__buttons__right__startAgain standard-link'}
                                                        onClick={reportAnotherClick}>
                                                        Start Again
                                                    </div>
                                                    <div className={className + '__body__inner__formBody__page__inner__buttons__right__back '}>
                                                        <BorderBlueButtonV2
                                                            text='Back'
                                                            disabled={false}
                                                            onClick={handleBackClick}/>
                                                    </div>
                                                    <div className={className + '__body__inner__formBody__page__inner__buttons__right__next '}>
                                                        <BorderBlueButtonV2
                                                            text='Next'
                                                            disabled={disableNext}
                                                            onClick={handleNextClick}/>
                                                    </div>
                                                </div>
                                            :
                                                <div className={className + '__body__inner__formBody__page__inner__buttons__right flex-row-center-1vw-gap'}>
                                                    <div className={className + '__body__inner__formBody__page__inner__buttons__right__startAgain standard-link'}
                                                        onClick={reportAnotherClick}>
                                                        Start Again
                                                    </div>
                                                    <div className={className + '__body__inner__formBody__page__inner__buttons__right__back '}>
                                                        <BorderBlueButtonV2
                                                            text='Back'
                                                            disabled={false}
                                                            onClick={handleBackClick}/>
                                                    </div>
                                                    <div className={className + '__body__inner__formBody__page__inner__buttons__right__next '}>
                                                        <BorderBlueButtonV2
                                                            text='Submit'
                                                            disabled={disableSubmit || disableNext || isCreationBlocked}
                                                            onClick={handleSubmitForm}/>
                                                    </div>
                                                </div>
                                        }
                                    </div>
                                </div>
                            </div>
                    }
                </div>
        </Drawer>

        {showNameCardForm && (
            <NameCardForm
                open={showNameCardForm}
                closePopup={toggleNameCardForm}
                reloadData={handleNameCardCreated}
            />
        )}
    </>
    )
}